// src/components/QuotePage.js
import React, { useState, useMemo } from "react";
import contactInfo from "../config/contactInfo";
// If you installed jspdf + html2canvas:
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const PACKAGES = [
  { id: "landing", name: "Landing Page", base: 350, description: "Single page, responsive, quick delivery" },
  { id: "spa", name: "SPA / Dashboard", base: 1200, description: "Authenticated SPA, components, API integration" },
  { id: "ecom", name: "E-commerce", base: 3500, description: "Product management, payments, admin" },
];

const ADDONS = [
  { id: "seo", name: "SEO Optimization", price: 150 },
  { id: "tests", name: "Automated Tests (Cypress/Playwright)", price: 300 },
  { id: "hosting", name: "Managed Hosting (1 year)", price: 120 },
  { id: "maintenance", name: "Monthly Maintenance (per month)", price: 80 },
];

function QuoteForm({ onRequest }) {
  const [selectedPackage, setSelectedPackage] = useState(PACKAGES[0].id);
  const [addons, setAddons] = useState({});
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState(40);
  const [notes, setNotes] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");

  const toggleAddon = (id) => setAddons(a => ({ ...a, [id]: !a[id] }));

  const pricing = useMemo(() => {
    const pkg = PACKAGES.find(p => p.id === selectedPackage);
    const base = pkg ? pkg.base : 0;
    const addonsTotal = ADDONS.reduce((sum, a) => sum + ((addons[a.id] ? a.price : 0)), 0);
    const hoursNum = parseFloat(hours) || 0;
    const hourlyTotal = hoursNum * (parseFloat(hourlyRate) || 0);
    const subtotal = base + addonsTotal + hourlyTotal;
    const vat = +(0.20 * subtotal).toFixed(2); // example 20% VAT; adjust for your needs
    const total = +(subtotal + vat).toFixed(2);
    return { base, addonsTotal, hourlyTotal: +hourlyTotal.toFixed(2), subtotal: +subtotal.toFixed(2), vat, total };
  }, [selectedPackage, addons, hours, hourlyRate]);

  function handleRequest() {
    // Basic client-side validation
    if (!clientName.trim() || !clientEmail.match(/^\S+@\S+\.\S+$/)) {
      alert("Please provide your name and a valid email to request the quote.");
      return;
    }
    const payload = {
      clientName, clientEmail, selectedPackage, addons: Object.keys(addons).filter(k => addons[k]), hours, hourlyRate, notes, pricing
    };
    onRequest(payload);
  }

  return (
    <div className="card p-3 shadow-sm" id="quoteCard">
      <div className="mb-3">
        <label className="form-label">Choose package</label>
        <select className="form-select" value={selectedPackage} onChange={e => setSelectedPackage(e.target.value)}>
          {PACKAGES.map(p => <option key={p.id} value={p.id}>{p.name} — €{p.base}</option>)}
        </select>
        <div className="small text-muted mt-1">{PACKAGES.find(p => p.id === selectedPackage).description}</div>
      </div>

      <div className="mb-3">
        <label className="form-label">Add-ons</label>
        <div className="d-flex flex-column">
          {ADDONS.map(a => (
            <label className="form-check form-check-inline" key={a.id}>
              <input type="checkbox" className="form-check-input" checked={!!addons[a.id]} onChange={() => toggleAddon(a.id)} />
              <span className="form-check-label ms-2">{a.name} (+€{a.price})</span>
            </label>
          ))}
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-sm-6">
          <label className="form-label">Custom hours (optional)</label>
          <input type="number" min="0" className="form-control" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 10" />
        </div>
        <div className="col-sm-6">
          <label className="form-label">Hourly rate (€)</label>
          <input type="number" min="0" className="form-control" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} />
        </div>
      </div>

      <hr />

      <div className="mb-3">
        <label className="form-label">Your name</label>
        <input className="form-control" value={clientName} onChange={e => setClientName(e.target.value)} />
      </div>

      <div className="mb-3">
        <label className="form-label">Your email</label>
        <input type="email" className="form-control" value={clientEmail} onChange={e => setClientEmail(e.target.value)} />
      </div>

      <div className="mb-3">
        <label className="form-label">Notes (project scope, deadline, etc.)</label>
        <textarea className="form-control" value={notes} onChange={e => setNotes(e.target.value)} rows="3" />
      </div>

      <div className="d-grid gap-2">
        <button type="button" className="btn btn-primary" onClick={handleRequest}>Request Quote</button>
      </div>
    </div>
  );
}

export default function QuotePage() {
  const [lastPayload, setLastPayload] = useState(null);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  async function handleRequest(payload) {
    setLastPayload(payload);
    // send to your Formspree / backend
    setSending(true);
    setMessage("");
    try {
      // Example: send to Formspree (replace endpoint)
      const FORM_ENDPOINT = "https://formspree.io/f/YOUR_FORMSPREE_ENDPOINT";
      const body = {
        to: contactInfo.email,
        subject: `Quote request from ${payload.clientName}`,
        payload
      };
      // Use your serverless endpoint or Formspree; this example posts JSON to a server you should implement.
      // If you don't have a backend, you can email the details to yourself using EmailJS or Formspree native form POST.
      await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: payload.clientName,
          email: payload.clientEmail,
          message: `Quote request\n\n${JSON.stringify(payload, null, 2)}`
        })
      });
      setMessage("Quote request sent — I'll contact you within 24 hours.");
    } catch (err) {
      console.error(err);
      setMessage("There was an error sending the request. Please email " + contactInfo.email);
    } finally {
      setSending(false);
    }
  }

  // simple render of price summary (recompute from payload)
  function PriceSummary({ payload }) {
    if (!payload) return <div className="text-muted">No selection yet</div>;
    const { pricing } = payload;
    return (
      <div>
        <h5 className="mb-3">Estimate</h5>
        <ul className="list-unstyled mb-3">
          <li>Base: €{pricing.base.toFixed(2)}</li>
          <li>Add-ons: €{pricing.addonsTotal.toFixed(2)}</li>
          <li>Hours: €{pricing.hourlyTotal.toFixed(2)}</li>
          <li>Subtotal: €{pricing.subtotal.toFixed(2)}</li>
          <li>VAT (20%): €{pricing.vat.toFixed(2)}</li>
        </ul>
        <div className="h4">Total: €{pricing.total.toFixed(2)}</div>
      </div>
    );
  }

  // Download current quote as PDF
  async function downloadPdf() {
    if (!lastPayload) {
      alert("Please create a quote first using the form.");
      return;
    }
    const node = document.getElementById("quoteSummary");
    if (!node) {
      alert("PDF element not found.");
      return;
    }
    const canvas = await html2canvas(node, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = 190; // A4 minus margins
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, "PNG", 10, 10, pdfWidth, pdfHeight);
    pdf.save(`quote-${(lastPayload.clientName||'client').replace(/\s+/g,'_')}.pdf`);
  }

  return (
    <main className="container py-5">
      <div className="row g-4">
        <div className="col-lg-6">
          <h1 className="fw-bold">Get a quote</h1>
          <p className="text-muted">Choose a package, add extras, or enter custom hours — get an instant estimate and request an official quote.</p>

          <QuoteForm onRequest={handleRequest} />
        </div>

        <div className="col-lg-6">
          <div className="card p-3 shadow-sm" id="quoteSummary">
            <h5 className="mb-2">Quote preview</h5>
            {lastPayload ? (
              <>
                <div className="small text-muted mb-2">Client: {lastPayload.clientName} — {lastPayload.clientEmail}</div>
                <div className="mb-2"><strong>Package:</strong> {PACKAGES.find(p=>p.id===lastPayload.selectedPackage).name}</div>
                <div className="mb-2"><strong>Addons:</strong> {lastPayload.addons && lastPayload.addons.length ? lastPayload.addons.join(", ") : "None"}</div>
                <div className="mb-2"><strong>Notes:</strong> {lastPayload.notes || "—"}</div>

                <hr />
                <PriceSummary payload={lastPayload} />
                <hr />
                <div className="small text-muted">Recipient: {contactInfo.email}</div>

                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-outline-primary" onClick={downloadPdf}>Download PDF</button>
                  <button className="btn btn-primary" onClick={() => handleRequest(lastPayload)} disabled={sending}>
                    {sending ? "Sending..." : "Request official quote"}
                  </button>
                </div>
                {message && <div className="alert alert-info mt-3">{message}</div>}
              </>
            ) : (
              <div className="text-muted">Fill the form to see a live estimate here.</div>
            )}
          </div>

          <div className="card mt-3 p-3 bg-light">
            <h6>Need help picking a package?</h6>
            <p className="small text-muted mb-0">If you're unsure, pick the closest package and add custom hours; we'll refine scope during discovery.</p>
          </div>
        </div>
      </div>
    </main>
  );
}
